import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Box,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { enrollMFA, verifyMFA } from '../../../queries';
import { QRCodeDisplay } from './QRCodeDisplay';
import { TokenInput } from './TokenInput';
import { BackupCodesDisplay } from './BackupCodesDisplay';

const MFASetupModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [token, setToken] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenError, setTokenError] = useState('');

  const handleEnroll = async () => {
    try {
      setIsLoading(true);
      const response = await enrollMFA();
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setStep(2);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast.error(
        error.response?.data?.message || 'Failed to initiate MFA enrollment'
      );
    }
  };

  const handleVerify = async () => {
    if (token.length !== 6) {
      setTokenError('Please enter a 6-digit code');
      return;
    }

    try {
      setIsLoading(true);
      setTokenError('');
      const response = await verifyMFA(token);
      setBackupCodes(response.data.backupCodes);
      setStep(3);
      setIsLoading(false);
      toast.success('MFA enabled successfully!');
    } catch (error) {
      setIsLoading(false);
      setTokenError(
        error.response?.data?.message || 'Invalid verification code'
      );
    }
  };

  const handleComplete = () => {
    if (onSuccess) {
      onSuccess();
    }
    handleClose();
  };

  const handleClose = () => {
    setStep(1);
    setQrCode('');
    setSecret('');
    setToken('');
    setBackupCodes([]);
    setTokenError('');
    onClose();
  };

  React.useEffect(() => {
    if (isOpen && step === 1) {
      handleEnroll();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay bg="rgba(0, 0, 0, 0.6)" />
      <ModalContent maxW="600px" borderRadius="12px">
        <ModalHeader
          bg="#1A73E8"
          color="white"
          borderTopRadius="12px"
          py="1.5rem"
        >
          <Text fontSize="20px" fontFamily="bold">
            {step === 1 && 'Setting Up Two-Factor Authentication'}
            {step === 2 && 'Verify Your Authenticator App'}
            {step === 3 && 'Save Your Backup Codes'}
          </Text>
        </ModalHeader>
        {step !== 3 && <ModalCloseButton color="white" />}
        <ModalBody py="2rem" px="2.5rem">
          {step === 1 && (
            <VStack spacing="1.5rem" align="center">
              <Text fontSize="14px" color="#666" textAlign="center">
                Initializing MFA setup...
              </Text>
            </VStack>
          )}

          {step === 2 && (
            <VStack spacing="2rem" align="stretch">
              <QRCodeDisplay qrCode={qrCode} secret={secret} />

              <Box>
                <Text
                  fontSize="14px"
                  fontFamily="medium"
                  color="#444"
                  mb="1rem"
                >
                  Enter the 6-digit code from your authenticator app:
                </Text>
                <TokenInput
                  value={token}
                  onChange={setToken}
                  error={tokenError}
                  placeholder="000000"
                />
              </Box>

              <Button
                bg="#1A73E8"
                color="white"
                fontSize="14px"
                fontFamily="medium"
                h="45px"
                onClick={handleVerify}
                isLoading={isLoading}
                isDisabled={token.length !== 6}
                _hover={{ bg: '#1558D6' }}
              >
                Verify and Enable MFA
              </Button>
            </VStack>
          )}

          {step === 3 && (
            <BackupCodesDisplay
              codes={backupCodes}
              onConfirm={handleComplete}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export { MFASetupModal };
